import type { Vec } from '../destruction/structure';
import type { PhysicsCity } from '../physics/world';
import { MinQueue } from '../core/priority';

/** A small physical bypass between walking-graph waypoints. Parked vehicles
 * can obstruct a link without covering either endpoint. Every candidate uses
 * the same supported capsule movement as the actual person; no collision mask
 * changes, position correction or traversal through missing floors. */
export function* localFootRoute(
  physics: PhysicsCity,
  start: Vec,
  target: Vec,
  stepHeight = 0.35,
): Generator<void, Vec[]> {
  const span = Math.hypot(target.x - start.x, target.z - start.z);
  if (span > 24 || Math.abs(start.y - target.y) > stepHeight + 0.05) return [];
  const step = 0.8,
    radius = Math.min(18, Math.max(7, span + 3));
  type Node = Vec & { ix: number; iz: number; cost: number; parent?: Node };
  const pending = new MinQueue<Node>(),
    visited = new Map<string, number>();
  const distance = (p: Vec) => Math.hypot(p.x - target.x, p.z - target.z);
  const first: Node = { ...start, ix: 0, iz: 0, cost: 0 };
  pending.push(first, distance(start));
  visited.set('0,0', 0);
  for (let count = 0; pending.size && count < 500; count++) {
    const p = pending.pop()!.value;
    if (p.cost > (visited.get(`${p.ix},${p.iz}`) ?? Infinity)) continue;
    if (
      distance(p) < 1.2 &&
      physics.sweepFoot(
        p,
        { x: target.x - p.x, y: target.y - p.y, z: target.z - p.z },
        0.24,
        undefined,
        stepHeight,
      ) >= 0.999
    ) {
      const result: Vec[] = [{ ...target }];
      for (let n: Node | undefined = p; n?.parent; n = n.parent) result.push({ x: n.x, y: n.y, z: n.z });
      return result.reverse();
    }
    for (const [dx, dz] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, 1],
      [-1, 1],
      [1, -1],
      [-1, -1],
    ]) {
      const ix = p.ix + dx,
        iz = p.iz + dz,
        cost = p.cost + Math.hypot(dx, dz) * step,
        key = `${ix},${iz}`;
      if (Math.hypot(ix, iz) * step > radius || cost >= (visited.get(key) ?? Infinity)) continue;
      const next = { x: start.x + ix * step, y: p.y, z: start.z + iz * step };
      const floor = physics.ground(next, stepHeight, stepHeight + 0.05);
      if (!floor || Math.abs(floor.y - p.y) > stepHeight) continue;
      next.y = floor.y;
      if (
        physics.sweepFoot(
          p,
          { x: next.x - p.x, y: next.y - p.y, z: next.z - p.z },
          0.24,
          undefined,
          stepHeight,
        ) < 0.999
      )
        continue;
      visited.set(key, cost);
      pending.push({ ...next, ix, iz, cost, parent: p }, cost + distance(next));
    }
    yield;
  }
  return [];
}
